import { useCallback, useState } from 'react'

/**
 * Submitting a moment to DiceTok to be clipped.
 *
 * There is no "full context" path. `verdict` is always ClippyJack's four-
 * second cut, and the hook models that honestly rather than pretending a 200
 * might mean the full clip is coming — a `fullClip` field that is never
 * populated would just invite someone to branch on it later.
 *
 * Posting now requires a real account (see auth.ts) — the endpoint 401s
 * without a valid bearer token. `submit` takes the caller's access token and a
 * `refresh` callback so a token that expired mid-session gets one retry after
 * a silent refresh, rather than failing the submission outright.
 */
export interface ClipState {
  pending: boolean
  verdict: string | null
  /** Set when ClippyJack answered from the fallback rather than the model. */
  degraded: boolean
  error: string | null
  /** Set when the request failed specifically because the caller isn't signed in. */
  needsLogin: boolean
}

const INITIAL: ClipState = { pending: false, verdict: null, degraded: false, error: null, needsLogin: false }

export function useClip() {
  const [state, setState] = useState<ClipState>(INITIAL)

  const postClip = useCallback(async (caption: string, moment: string, token: string) => {
    return fetch('/api/clip', {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify({ caption, moment }),
    })
  }, [])

  const submit = useCallback(
    async (caption: string, moment: string, accessToken: string | null, refresh: () => Promise<string | null>) => {
      if (!moment.trim()) return
      if (!accessToken) {
        setState({ ...INITIAL, needsLogin: true, error: 'Sign in to submit a moment.' })
        return
      }
      setState({ ...INITIAL, pending: true })
      try {
        let res = await postClip(caption, moment, accessToken)
        if (res.status === 401) {
          const fresh = await refresh()
          if (!fresh) {
            setState({ ...INITIAL, needsLogin: true, error: 'Your session expired. Sign in again.' })
            return
          }
          res = await postClip(caption, moment, fresh)
        }

        const data = (await res.json().catch(() => null)) as
          | { verdict?: string; degraded?: boolean; error?: string }
          | null

        if (res.status === 401 || res.status === 403) {
          setState({ ...INITIAL, needsLogin: true, error: 'Sign in to submit a moment.' })
          return
        }
        if (!res.ok || !data?.verdict) {
          // Never surface a raw failure as silence — that was the obsidia lesson.
          setState({ ...INITIAL, error: 'ClippyJack is backed up. Try again shortly.' })
          return
        }

        setState({ ...INITIAL, verdict: data.verdict, degraded: !!data.degraded })
      } catch {
        setState({ ...INITIAL, error: 'Could not reach ClippyJack.' })
      }
    },
    [postClip],
  )

  const dismiss = useCallback(() => setState(INITIAL), [])

  return { ...state, submit, dismiss }
}
